import { useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const EMPTY_GROUP = { name: "", required: true, min: 1, max: 1, options: [] };
const EMPTY_OPTION = { name: "", price: 0, image_url: "" };

const OptionPhoto = ({ value, onChange, testid }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid={testid} onClick={() => ref.current?.click()} disabled={busy}
        className="relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-lg border border-dashed border-white/20 bg-white/5 text-slate-400 hover:border-primo-500/60">
        {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-4 w-4" />}
        {busy && <span className="absolute inset-0 grid place-items-center bg-black/60"><Loader2 className="h-4 w-4 animate-spin text-primo-400" /></span>}
      </button>
      <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" />
    </>
  );
};

export const AddonsEditor = ({ addons, onChange }) => {
  const update = (i, patch) => onChange(addons.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  return (
    <div className="space-y-2 rounded-xl border border-primo-500/20 bg-primo-500/[0.04] p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Label>Adicionais (opcional, cobrado à parte)</Label>
          <p className="text-xs text-slate-500">O cliente escolhe se quiser — nunca é obrigatório</p>
        </div>
        <button type="button" data-testid="add-addon-button" onClick={() => onChange([...addons, { ...EMPTY_OPTION }])}
          className="flex shrink-0 items-center gap-1 rounded-full bg-primo-500 px-3 py-1 text-xs font-bold text-white hover:bg-primo-400">
          <Plus className="h-3 w-3" /> Adicional
        </button>
      </div>
      {addons.map((a, i) => (
        <div key={i} className="flex items-center gap-2">
          <OptionPhoto testid={`addon-photo-${i}`} value={a.image_url} onChange={(url) => update(i, { image_url: url })} />
          <Input data-testid={`addon-name-${i}`} placeholder="Ex.: Gelo de coco 2kg" value={a.name} onChange={(e) => update(i, { name: e.target.value })} />
          <Input type="number" min={0} step="0.01" data-testid={`addon-price-${i}`} placeholder="R$" className="w-24"
            value={a.price ?? 0} onChange={(e) => update(i, { price: Number(e.target.value || 0) })} />
          <button type="button" data-testid={`remove-addon-${i}`} onClick={() => onChange(addons.filter((_, idx) => idx !== i))}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};

export const OptionGroupsEditor = ({ groups, onChange }) => {
  const update = (i, patch) => onChange(groups.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  const updateOption = (gi, oi, patch) =>
    update(gi, { options: groups[gi].options.map((o, idx) => (idx === oi ? { ...o, ...patch } : o)) });

  return (
    <div className="space-y-3 rounded-xl border border-white/[0.08] bg-black/20 p-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <Label>Opções do combo</Label>
          <p className="text-xs text-slate-500">Escolhas inclusas no preço, ex.: "Whisky (escolha 1)" ou "Red Bull (escolha 5)"</p>
        </div>
        <button type="button" data-testid="add-option-group-button" onClick={() => onChange([...groups, { ...EMPTY_GROUP, options: [] }])}
          className="flex shrink-0 items-center gap-1 rounded-full bg-primo-500 px-3 py-1 text-xs font-bold text-white hover:bg-primo-400">
          <Plus className="h-3 w-3" /> Grupo
        </button>
      </div>

      {groups.map((g, i) => (
        <div key={i} data-testid={`option-group-${i}`} className="space-y-3 rounded-lg border border-white/[0.08] p-3">
          <div className="flex items-center gap-2">
            <Input data-testid={`option-group-name-${i}`} placeholder="Nome do grupo (ex.: Adicionais)"
              value={g.name} onChange={(e) => update(i, { name: e.target.value })} />
            <button type="button" data-testid={`remove-option-group-${i}`} onClick={() => onChange(groups.filter((_, idx) => idx !== i))}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-3 items-end gap-2">
            <div><Label className="text-xs">Mínimo</Label>
              <Input type="number" min={0} data-testid={`option-group-min-${i}`} value={g.min}
                onChange={(e) => update(i, { min: parseInt(e.target.value || "0", 10) })} /></div>
            <div><Label className="text-xs">Máximo</Label>
              <Input type="number" min={1} data-testid={`option-group-max-${i}`} value={g.max}
                onChange={(e) => update(i, { max: parseInt(e.target.value || "1", 10) })} /></div>
            <div className="flex items-center gap-2 pb-2">
              <Switch data-testid={`option-group-required-${i}`} checked={g.required} onCheckedChange={(v) => update(i, { required: v })} />
              <Label className="text-xs">Obrigatório</Label>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Opções (nome, preço adicional e foto)</Label>
              <button type="button" data-testid={`add-option-${i}`} onClick={() => update(i, { options: [...g.options, { ...EMPTY_OPTION }] })}
                className="flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-xs font-semibold hover:bg-white/20"><Plus className="h-3 w-3" /> Opção</button>
            </div>
            {g.options.map((o, oi) => (
              <div key={oi} className="flex items-center gap-2">
                <OptionPhoto testid={`option-photo-${i}-${oi}`} value={o.image_url} onChange={(url) => updateOption(i, oi, { image_url: url })} />
                <Input data-testid={`option-name-${i}-${oi}`} placeholder="Nome da opção" value={o.name} onChange={(e) => updateOption(i, oi, { name: e.target.value })} />
                <Input type="number" min={0} step="0.01" data-testid={`option-price-${i}-${oi}`} placeholder="+R$" className="w-24"
                  value={o.price ?? 0} onChange={(e) => updateOption(i, oi, { price: Number(e.target.value || 0) })} />
                <button type="button" data-testid={`remove-option-${i}-${oi}`} onClick={() => update(i, { options: g.options.filter((_, idx) => idx !== oi) })}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
